/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/rules-of-hooks */
import { observer } from "mobx-react";
import {
  createStyles,
  // createStyles,
  Flex,
} from "@mantine/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas, useThree } from "@react-three/fiber";
import { Environment, ContactShadows, OrbitControls, PerspectiveCamera, Html } from "@react-three/drei";
import { useStore } from "../../../../hooks/use-store";
import { useRequest } from "ahooks";
import { NOTIFICATION_SERVICE } from "../../../../components/notifications/NotificationService";
import { PromptField } from "./PromptField";
import { PipelineParameters } from "../Pipeline";
import { priceMap } from "../Router";
import Car from "./Car";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import { EXRLoader } from "three/examples/jsm/loaders/EXRLoader";
import { GroundProjectedEnv } from "three-stdlib";

export type GalleryItem = {
  images: { url: string, preview: string }[];
  previewHdrl: string;
  enhancedHdrlFile: string;
  modelVersion?: string;
  loraName?: string;
}


const useStyles = createStyles((theme) => ({
  uploadContainer: {
    backgroundColor: '#4f4f4f',
    borderRadius: '4px',
    height: '288px',
    overflow: 'hidden',
  },
  widgetRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    '> div': {
      textAlign: 'center',
    }
  },
  viewerContainer: {
    width: '100%',
    height: '720px',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
  },
  hdrControls: {
    gap: '12px',
    alignItems: 'center',
  },
}));

const Element = (model: {
  buy: (params: PipelineParameters[]) => Promise<void>;
  loading: boolean
}) => {

  const {
    dataStore: { spaceStore, appStore },
    uiStore: { authStore },
  } = useStore();

  const secretModeEnabled = authStore.currentUser?.id === 'user_ncW1SxIG5X3mwQGtmPp5Z' || authStore.currentUser?.id === 'user_ztvePOlS8Ylq0N0c5wFhK' || authStore.currentUser?.id === 'user_RzgcIMD1nu3VgXxx1ohCx' || authStore.currentUser?.id === 'user_bwxZh45ttxCf6U3MQeJCc'
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loading: runAsyncConvertLoading, runAsync: runAsyncConvert } = useRequest(
    async (model: { url: string, removeBackground?: boolean }) => appStore.convertImage(model),
    {
      manual: true,
      onSuccess: (res) => {
        if (res.error) {
          NOTIFICATION_SERVICE.errorNotification(
            "Sorry, service is unavailable due to high demand. Please try later"
          );
        } else {
          return res.url
        }
      },
    }
  );

  const { classes } = useStyles();

  const { loading: runAsyncVisionLoading, runAsync: runAsyncVision } = useRequest(
    async (model: { url: string }) => appStore.vision({
      url: model.url,
    }),
    {
      manual: true,
      onSuccess: (res) => {
        if (res.error) {
          NOTIFICATION_SERVICE.errorNotification(
            "Sorry, service is unavailable due to high demand. Please try later"
          );
        } else {
          return res.url
        }
      },
    }
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loading: runAsyncConvertBase64Loading, runAsync: runAsyncConvertBase64 } = useRequest(
    async (model: { url: string }) => appStore.convertImageBase64({
      url: model.url,
    }),
    {
      manual: true,
      onSuccess: (res) => {
        if (res.error) {
          NOTIFICATION_SERVICE.errorNotification(
            "Sorry, service is unavailable due to high demand. Please try later"
          );
        } else {
          return res.url
        }
      },
    }
  );

  // Local viewer state
  const [hdrUrl, setHdrUrl] = useState<string | null>(null);
  const [exposure, setExposure] = useState<number>(0.85);
  const [hdrFile, setHdrFile] = useState<File | null>(null);
  const [hdrTex, setHdrTex] = useState<THREE.Texture | null>(null);
  const [hdrLoading, setHdrLoading] = useState<boolean>(false);
  const [hdrError, setHdrError] = useState<string | null>(null);
  const toneMapping: 'ACESFilmic' = 'ACESFilmic';
  const [groundHeight, setGroundHeight] = useState<number>(35);
  const [groundRadius, setGroundRadius] = useState<number>(100);
  const [groundScale, setGroundScale] = useState<number>(200);
  const [fisheye, setFisheye] = useState<boolean>(false);
  const [fov, setFov] = useState<number>(50);
  const [carRotationDeg, setCarRotationDeg] = useState<number>(-45);

  // Car material controls (lifted from Car)
  const [paintColor, setPaintColor] = useState<string>('#ffdf71')
  const [paintMetalness, setPaintMetalness] = useState<number>(0.2)
  const [paintRoughness, setPaintRoughness] = useState<number>(0)
  const [paintClearcoat, setPaintClearcoat] = useState<number>(1)
  const [paintEnvMapIntensity, setPaintEnvMapIntensity] = useState<number>(1.5)
  const [paintAoMapIntensity, setPaintAoMapIntensity] = useState<number>(1.5)
  const [paintRoughnessMapIntensity, setPaintRoughnessMapIntensity] = useState<number>(2)
  const [carEnvIntensity, setCarEnvIntensity] = useState<number>(1.5)

  const [chromeMetalness, setChromeMetalness] = useState<number>(1)
  const [chromeRoughness, setChromeRoughness] = useState<number>(0.3)
  const [chromeColor, setChromeColor] = useState<string>('#ffffff')

  const [glassColor, setGlassColor] = useState<string>('#ffffff')
  const [glassOpacity, setGlassOpacity] = useState<number>(0.6)

  const [plasticsRoughness, setPlasticsRoughness] = useState<number>(0.8)

  const [lightsEmissiveIntensity, setLightsEmissiveIntensity] = useState<number>(50)

  const [tireColor, setTireColor] = useState<string>('#000000')
  const [tireRoughness, setTireRoughness] = useState<number>(0.7)

  // Canvas/renderer refs for screenshots
  const canvasStateRef = useRef<{
    gl: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.Camera;
  } | null>(null);
  const contactShadowsRef = useRef<THREE.Group | null>(null)

  const downloadDataUrl = (dataUrl: string, filename: string) => {
    const link = document.createElement('a')
    link.href = dataUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadScreenshot = () => {
    const state = canvasStateRef.current
    if (!state) return
    const { gl } = state
    const canvas = gl.domElement as HTMLCanvasElement
    // Ensure the latest frame is drawn before grabbing pixels
    requestAnimationFrame(() => {
      try {
        const dataUrl = canvas.toDataURL('image/png')
        downloadDataUrl(dataUrl, `screenshot-${Date.now()}.png`)
      } catch {
        NOTIFICATION_SERVICE.errorNotification('Failed to capture screenshot')
      }
    })
  }

  const downloadScreenshotCarOnly = () => {
    const state = canvasStateRef.current
    if (!state) return
    const { gl, scene, camera } = state
    const canvas = gl.domElement as HTMLCanvasElement

    // Save state
    const prevBackground = scene.background
    const prevClearColor = new THREE.Color()
    gl.getClearColor(prevClearColor)
    const prevClearAlpha = gl.getClearAlpha()

    // Hide HDR background, keep environment for lighting
    scene.background = null
    gl.setClearColor(prevClearColor, 0) // make background transparent
    const hiddenObjects: THREE.Object3D[] = []
    scene.traverse((obj) => {
      // Hide GroundProjectedEnv objects (environment ground projection)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyObj = obj as any
      if (anyObj instanceof GroundProjectedEnv || anyObj.isGroundProjectedEnv || obj.constructor?.name === 'GroundProjectedEnv') {
        if (obj.visible) {
          hiddenObjects.push(obj)
          obj.visible = false
        }
      }
    })
    if (contactShadowsRef.current && contactShadowsRef.current.visible) {
      hiddenObjects.push(contactShadowsRef.current)
      contactShadowsRef.current.visible = false
    }

    // Render one frame with transparent background and capture
    requestAnimationFrame(() => {
      try {
        const prevAutoClear = gl.autoClear
        gl.autoClear = true
        gl.clear(true, true, true)
        gl.render(scene, camera)
        gl.autoClear = prevAutoClear
        const dataUrl = canvas.toDataURL('image/png')
        downloadDataUrl(dataUrl, `screenshot-car-only-${Date.now()}.png`)
      } catch {
        NOTIFICATION_SERVICE.errorNotification('Failed to capture car-only screenshot')
      } finally {
        // Restore state
        scene.background = prevBackground
        gl.setClearColor(prevClearColor, prevClearAlpha)
        hiddenObjects.forEach((obj) => (obj.visible = true))
      }
    })
  }

  const onSelectHdr = (file: File | null) => {
    if (!file) {
      if (hdrUrl) URL.revokeObjectURL(hdrUrl);
      setHdrUrl(null);
      setHdrFile(null);
      if (hdrTex) {
        hdrTex.dispose();
      }
      setHdrTex(null);
      setHdrError(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    if (hdrUrl) URL.revokeObjectURL(hdrUrl);
    setHdrUrl(objectUrl);
    setHdrFile(file);
    // Preload texture before rendering scene
    setHdrLoading(true);
    setHdrError(null);
    const name = (file.name || '').toLowerCase()
    const type = (file.type || '').toLowerCase()
    const url = objectUrl
    const isHDR = name.endsWith('.hdr') || type.includes('radiance') || type.includes('hdr')
    const isEXR = name.endsWith('.exr') || type.includes('exr')
    const isImage = /(png|jpg|jpeg|webp)/.test(name) || /(png|jpg|jpeg|webp)/.test(type)
    const onSuccess = (tex: THREE.Texture) => {
      tex.mapping = THREE.EquirectangularReflectionMapping
      if (hdrTex) hdrTex.dispose()
      setHdrTex(tex)
      setHdrLoading(false)
    }
    const onFail = () => {
      setHdrLoading(false)
      setHdrError('Failed to load HDRI')
      NOTIFICATION_SERVICE.errorNotification('Failed to load HDRI. Please try a different file.')
    }
    if (isHDR) {
      const dataTypes = [
        // Try common HDR data types in order
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (THREE as any).HalfFloatType,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (THREE as any).FloatType,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (THREE as any).UnsignedByteType,
      ].filter(Boolean)
      let attemptIndex = 0
      const tryLoad = () => {
        const loader = new RGBELoader()
        const dataType = dataTypes[attemptIndex]
        if (dataType) {
          loader.setDataType(dataType)
        }
        loader.load(url, onSuccess, undefined, () => {
          attemptIndex++
          if (attemptIndex < dataTypes.length) {
            tryLoad()
          } else {
            new THREE.TextureLoader().load(url, onSuccess, undefined, onFail)
          }
        })
      }
      tryLoad()
    } else if (isEXR) {
      new EXRLoader().load(url, onSuccess, undefined, () => {
        new THREE.TextureLoader().load(url, onSuccess, undefined, onFail)
      })
    } else if (isImage) {
      new THREE.TextureLoader().load(url, onSuccess, undefined, onFail)
    } else {
      new RGBELoader().load(url, onSuccess, undefined, () => {
        new EXRLoader().load(url, onSuccess, undefined, () => {
          new THREE.TextureLoader().load(url, onSuccess, undefined, onFail)
        })
      })
    }
  };

  const hdrAccept = useMemo(() => '.hdr,.exr,.png,.jpg,.jpeg', []);


  return (
    <Flex direction={'column'} w="100%" pos={'relative'} gap={12} mt={20} mb={60}>

      <Flex direction={'column'} gap={12}>
        <Flex className={classes.hdrControls}>
          <input
            type="file"
            accept={hdrAccept}
            onChange={(e) => onSelectHdr(e.target.files?.[0] || null)}
          />


        </Flex>
        {!hdrTex ? (
          <div className={classes.viewerContainer} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbbbbb' }}>
            {hdrLoading ? 'Loading HDRI…' : 'Choose an HDRI to start'}
            {hdrError ? <span style={{ marginLeft: 12, color: '#ff6b6b' }}>{hdrError}</span> : null}
          </div>
        ) : (
          <div className={classes.viewerContainer}>
            <div style={{ zIndex: 1000, position: 'fixed', top: 0, right: 0, padding: 12, borderRadius: 8, background: 'rgba(20,20,20,0.8)', color: '#ddd', display: 'grid', gap: 8, width: 280, fontFamily: 'Inter, system-ui, Arial, sans-serif', fontSize: 12 }}>
              <div style={{ display: 'grid', gap: 8 }}>
                <button onClick={downloadScreenshot} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #2a2a2a', background: '#2b2b2b', color: '#eee', cursor: 'pointer' }}>
                  Download screenshot
                </button>
                <button onClick={downloadScreenshotCarOnly} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #2a2a2a', background: '#2b2b2b', color: '#eee', cursor: 'pointer' }}>
                  Download car-only (no HDR visible)
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#bbb' }}>Exposure</span>
                <input
                  type="range"
                  min={0}
                  max={2}
                  step={0.001}
                  value={exposure}
                  onChange={(e) => setExposure(parseFloat(e.target.value))}
                  style={{ width: '200px' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#bbb' }}>Fisheye</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={fisheye}
                    onChange={(e) => {
                      const checked = e.target.checked
                      setFisheye(checked)
                      setFov(checked ? 100 : 50)
                    }}
                  />
                  <span style={{ color: '#999' }}>{fisheye ? 'On' : 'Off'}</span>
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#bbb' }}>FOV</span>
                <input
                  type="range"
                  min={20}
                  max={120}
                  step={1}
                  value={fov}
                  onChange={(e) => setFov(parseFloat(e.target.value))}
                  style={{ width: '140px' }}
                />
                <span style={{ color: '#999', minWidth: 28, textAlign: 'right' }}>{fov}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#bbb' }}>Car rotation</span>
                <input
                  type="range"
                  min={-180}
                  max={180}
                  step={1}
                  value={carRotationDeg}
                  onChange={(e) => setCarRotationDeg(parseFloat(e.target.value))}
                  style={{ width: '140px' }}
                />
                <span style={{ color: '#999', minWidth: 28, textAlign: 'right' }}>{carRotationDeg}°</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#bbb' }}>Ground height</span>
                <input
                  type="range"
                  min={1}
                  max={200}
                  step={1}
                  value={groundHeight}
                  onChange={(e) => setGroundHeight(parseFloat(e.target.value))}
                  style={{ width: '140px' }}
                />
                <span style={{ color: '#999', minWidth: 28, textAlign: 'right' }}>{groundHeight}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#bbb' }}>Ground radius</span>
                <input
                  type="range"
                  min={10}
                  max={500}
                  step={1}
                  value={groundRadius}
                  onChange={(e) => setGroundRadius(parseFloat(e.target.value))}
                  style={{ width: '140px' }}
                />
                <span style={{ color: '#999', minWidth: 28, textAlign: 'right' }}>{groundRadius}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#bbb' }}>Ground scale</span>
                <input
                  type="range"
                  min={10}
                  max={500}
                  step={1}
                  value={groundScale}
                  onChange={(e) => setGroundScale(parseFloat(e.target.value))}
                  style={{ width: '140px' }}
                />
                <span style={{ color: '#999', minWidth: 28, textAlign: 'right' }}>{groundScale}</span>
              </div>
              <strong style={{ color: '#1e1d1d' }}>Car material controls</strong>
              <div style={{ display: 'grid', gap: 6 }}>
                <label>
                  <span>Car HDR intensity</span>
                  <input type="range" min={0} max={5} step={0.05} value={carEnvIntensity} onChange={(e) => setCarEnvIntensity(parseFloat(e.target.value))} />
                </label>
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid #333', margin: '8px 0' }} />
              <div style={{ display: 'grid', gap: 6 }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span>Paint color</span>
                  <input type="color" value={paintColor} onChange={(e) => setPaintColor(e.target.value)} />
                </label>
                <label>
                  <span>Paint metalness: {paintMetalness.toFixed(2)}</span>
                  <input type="range" min={0} max={1} step={0.01} value={paintMetalness} onChange={(e) => setPaintMetalness(parseFloat(e.target.value))} />
                </label>
                <label>
                  <span>Paint roughness: {paintRoughness.toFixed(2)}</span>
                  <input type="range" min={0} max={1} step={0.01} value={paintRoughness} onChange={(e) => setPaintRoughness(parseFloat(e.target.value))} />
                </label>
                <label>
                  <span>Paint clearcoat: {paintClearcoat.toFixed(2)}</span>
                  <input type="range" min={0} max={1} step={0.01} value={paintClearcoat} onChange={(e) => setPaintClearcoat(parseFloat(e.target.value))} />
                </label>
                <label>
                  <span>Paint envMapIntensity: {paintEnvMapIntensity.toFixed(2)}</span>
                  <input type="range" min={0} max={5} step={0.05} value={paintEnvMapIntensity} onChange={(e) => setPaintEnvMapIntensity(parseFloat(e.target.value))} />
                </label>
                <label>
                  <span>Paint aoMapIntensity: {paintAoMapIntensity.toFixed(2)}</span>
                  <input type="range" min={0} max={5} step={0.05} value={paintAoMapIntensity} onChange={(e) => setPaintAoMapIntensity(parseFloat(e.target.value))} />
                </label>
                <label>
                  <span>Paint roughnessMapIntensity: {paintRoughnessMapIntensity.toFixed(2)}</span>
                  <input type="range" min={0} max={5} step={0.05} value={paintRoughnessMapIntensity} onChange={(e) => setPaintRoughnessMapIntensity(parseFloat(e.target.value))} />
                </label>
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid #333', margin: '8px 0' }} />
              <div style={{ display: 'grid', gap: 6 }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span>Chrome color</span>
                  <input type="color" value={chromeColor} onChange={(e) => setChromeColor(e.target.value)} />
                </label>
                <label>
                  <span>Chrome metalness: {chromeMetalness.toFixed(2)}</span>
                  <input type="range" min={0} max={1} step={0.01} value={chromeMetalness} onChange={(e) => setChromeMetalness(parseFloat(e.target.value))} />
                </label>
                <label>
                  <span>Chrome roughness: {chromeRoughness.toFixed(2)}</span>
                  <input type="range" min={0} max={1} step={0.01} value={chromeRoughness} onChange={(e) => setChromeRoughness(parseFloat(e.target.value))} />
                </label>
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid #333', margin: '8px 0' }} />
              <div style={{ display: 'grid', gap: 6 }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span>Glass color</span>
                  <input type="color" value={glassColor} onChange={(e) => setGlassColor(e.target.value)} />
                </label>
                <label>
                  <span>Glass opacity: {glassOpacity.toFixed(2)}</span>
                  <input type="range" min={0} max={1} step={0.01} value={glassOpacity} onChange={(e) => setGlassOpacity(parseFloat(e.target.value))} />
                </label>
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid #333', margin: '8px 0' }} />
              <div style={{ display: 'grid', gap: 6 }}>
                <label>
                  <span>Plastics roughness: {plasticsRoughness.toFixed(2)}</span>
                  <input type="range" min={0} max={1} step={0.01} value={plasticsRoughness} onChange={(e) => setPlasticsRoughness(parseFloat(e.target.value))} />
                </label>
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid #333', margin: '8px 0' }} />
              <div style={{ display: 'grid', gap: 6 }}>
                <label>
                  <span>Lights emissive intensity: {lightsEmissiveIntensity.toFixed(0)}</span>
                  <input type="range" min={0} max={200} step={1} value={lightsEmissiveIntensity} onChange={(e) => setLightsEmissiveIntensity(parseFloat(e.target.value))} />
                </label>
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid #333', margin: '8px 0' }} />
              <div style={{ display: 'grid', gap: 6 }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span>Tire color</span>
                  <input type="color" value={tireColor} onChange={(e) => setTireColor(e.target.value)} />
                </label>
                <label>
                  <span>Tire roughness: {tireRoughness.toFixed(2)}</span>
                  <input type="range" min={0} max={1} step={0.01} value={tireRoughness} onChange={(e) => setTireRoughness(parseFloat(e.target.value))} />
                </label>
              </div>
            </div>
            <Canvas
            
              gl={{ preserveDrawingBuffer: true, alpha: true }}
              onCreated={({ gl, scene, camera }) => {
                canvasStateRef.current = { gl, scene, camera }
              }}
            >

              <Environment files="/ma-MAG_8092_Abo.hdr" ground={{ height: groundHeight, radius: groundRadius, scale: groundScale }} />
              {/* <EnvironmentFromTexture
                  texture={hdrTex}
                  ground={{ height: groundHeight, radius: groundRadius, scale: groundScale }}
                /> */}
              <Car
                position={[-8, 0, -2]}
                scale={20}
                rotation={[0, (carRotationDeg * Math.PI) / 180, 0]}
                paintColor={paintColor}
                paintMetalness={paintMetalness}
                paintRoughness={paintRoughness}
                paintClearcoat={paintClearcoat}
                paintEnvMapIntensity={paintEnvMapIntensity}
                carEnvIntensity={carEnvIntensity}
                paintAoMapIntensity={paintAoMapIntensity}
                paintRoughnessMapIntensity={paintRoughnessMapIntensity}
                chromeMetalness={chromeMetalness}
                chromeRoughness={chromeRoughness}
                chromeColor={chromeColor}
                glassColor={glassColor}
                glassOpacity={glassOpacity}
                plasticsRoughness={plasticsRoughness}
                lightsEmissiveIntensity={lightsEmissiveIntensity}
                tireColor={tireColor}
                tireRoughness={tireRoughness}
              />
              <ContactShadows ref={contactShadowsRef as unknown as React.Ref<THREE.Group>} renderOrder={2} frames={1} resolution={1024} scale={120} blur={2} opacity={0.6} far={100} />
              {/* <ContactShadows renderOrder={2} frames={1} resolution={1024} scale={120} blur={2} opacity={0.6} far={100} /> */}
              <OrbitControls enableZoom={true} enablePan={true} minPolarAngle={0} maxPolarAngle={Math.PI / 2.25} makeDefault />
              <PerspectiveCamera makeDefault position={[45, 45, 10]} fov={fov} />


              <Tone mapping={toneMapping} exposure={exposure} />
            </Canvas>
          </div>
        )}
      </Flex>
      <PromptField
        credits={priceMap.THREE_D}
        onClick={async () => {


          // const pipeline: PipelineParameters = {
          //   provider: 'comfy',
          //   type: 'controlV2',
          //   color: spaceStore.carColor,
          //   loraName: spaceStore.selectedModel!.name.includes('.safetensors') ? spaceStore.selectedModel!.name : spaceStore.selectedModel!.name + '.safetensors',
          //   carLoraName: spaceStore.secondLoraName,
          //   streight: '0.5',
          //   cameraInfo: [
          //     { key: 'Camera Position X', value: cameraPosition.x.toFixed(2) },
          //     { key: 'Camera Position Y', value: cameraPosition.y.toFixed(2) },
          //     { key: 'Camera Position Z', value: cameraPosition.z.toFixed(2) },
          //     { key: 'Camera FOV', value: cameraFov.toFixed(2) },
          //     { key: 'Camera Height', value: (cameraHeight / 100).toFixed(2) + 'm' },
          //     { key: 'Distance to Car', value: (distanceToCar / 100).toFixed(2) + 'm' },
          //     { key: 'Focal Length', value: focalLength.toFixed(0) + 'mm' },
          //   ],
          //   loraStreight: '1.3',
          //   cameraCode: encodeSettings(),
          //   carLoraStreight: '0.9',
          //   credits: priceMap.THREE_D,
          //   inputSrc: url,
          //   prompt: spaceStore.prompt,
          //   negativePrompt: '',
          //   srcset: [],
          // }
          // await model.buy([pipeline])
        }}
        value={spaceStore.prompt}
        onChangeEnd={(value) => {
          spaceStore.setPrompt(value)
        }}
        loading={model.loading}
        placeholder="Describe an image and click generate"
      >
        <Flex gap={8} p={10} wrap={'wrap'} pr={'100px'} w={'100%'} />
      </PromptField>
    </Flex>
  );
};

export const ThreeDModelHDR = observer(Element);

function Tone({ mapping, exposure }: { mapping: 'ACESFilmic', exposure: number }) {
  const gl = useThree((state) => state.gl)
  useEffect(() => {
    const prevFrag = THREE.ShaderChunk.tonemapping_pars_fragment
    const prevTonemapping = gl.toneMapping
    const prevTonemappingExp = gl.toneMappingExposure
    THREE.ShaderChunk.tonemapping_pars_fragment = THREE.ShaderChunk.tonemapping_pars_fragment.replace(
      'vec3 CustomToneMapping( vec3 color ) { return color; }',
      `float startCompression = 0.8 - 0.04;
       float desaturation = 0.15;
       vec3 CustomToneMapping( vec3 color ) {
         color *= toneMappingExposure;
         float x = min(color.r, min(color.g, color.b));
         float offset = x < 0.08 ? x - 6.25 * x * x : 0.04;
         color -= offset;
         float peak = max(color.r, max(color.g, color.b));
         if (peak < startCompression) return color;
         float d = 1. - startCompression;
         float newPeak = 1. - d * d / (peak + d - startCompression);
         color *= newPeak / peak;
         float g = 1. - 1. / (desaturation * (peak - newPeak) + 1.);
         return mix(color, vec3(1, 1, 1), g);
       }`,
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gl.toneMapping = (THREE as any)[mapping + 'ToneMapping']
    gl.toneMappingExposure = exposure
    return () => {
      gl.toneMapping = prevTonemapping
      gl.toneMappingExposure = prevTonemappingExp
      THREE.ShaderChunk.tonemapping_pars_fragment = prevFrag
    }
  }, [mapping, exposure, gl])
  return null
}

// Fallback Fisheye wrapper (no-op) to match requested JSX structure when the library version lacks it
function Fisheye(model: { resolution?: number, zoom?: number, children: React.ReactNode }) {
  return <>{model.children}</>
}

function EnvironmentFromFile({ file }: { file: File }) {
  const { scene, gl } = useThree()
  useEffect(() => {
    let disposed = false
    const pmrem = new THREE.PMREMGenerator(gl)
    pmrem.compileEquirectangularShader()

    const revokeLater: string[] = []
    let currentEnvRT: THREE.WebGLRenderTarget | null = null
    let currentBgTex: THREE.Texture | null = null

    const setEnv = (tex: THREE.Texture) => {
      if (disposed) return
      // Dispose previous
      if (currentEnvRT) {
        currentEnvRT.dispose()
      }
      if (currentBgTex) {
        currentBgTex.dispose()
      }
      // Create env map for lighting
      const envRT = pmrem.fromEquirectangular(tex)
      const envMap = envRT.texture
      scene.environment = envMap
      // Use original equirectangular texture as visible background
      scene.background = tex
      currentEnvRT = envRT
      currentBgTex = tex
    }

    const name = (file.name || '').toLowerCase()
    const type = (file.type || '').toLowerCase()
    const url = URL.createObjectURL(file)
    revokeLater.push(url)

    const isHDR = name.endsWith('.hdr') || type.includes('radiance') || type.includes('hdr')
    const isEXR = name.endsWith('.exr') || type.includes('exr')
    const isImage = /(png|jpg|jpeg|webp)/.test(name) || /(png|jpg|jpeg|webp)/.test(type)

    if (isHDR) {
      new RGBELoader().load(
        url,
        (tex) => {
          if (disposed) return
          tex.mapping = THREE.EquirectangularReflectionMapping
          setEnv(tex)
        },
        undefined,
        () => {
          // fallback: try as standard image if parse fails
          if (disposed) return
          new THREE.TextureLoader().load(
            url,
            (tex) => {
              tex.mapping = THREE.EquirectangularReflectionMapping
              setEnv(tex)
            }
          )
        }
      )
    } else if (isEXR) {
      new EXRLoader().load(
        url,
        (tex) => {
          if (disposed) return
          tex.mapping = THREE.EquirectangularReflectionMapping
          setEnv(tex)
        },
        undefined,
        () => {
          if (disposed) return
          new THREE.TextureLoader().load(
            url,
            (tex) => {
              tex.mapping = THREE.EquirectangularReflectionMapping
              setEnv(tex)
            }
          )
        }
      )
    } else if (isImage) {
      new THREE.TextureLoader().load(
        url,
        (tex) => {
          if (disposed) return
          tex.mapping = THREE.EquirectangularReflectionMapping
          setEnv(tex)
        }
      )
    } else {
      // Unknown type, try HDR then EXR then image
      new RGBELoader().load(
        url,
        (tex) => {
          if (disposed) return
          tex.mapping = THREE.EquirectangularReflectionMapping
          setEnv(tex)
        },
        undefined,
        () => {
          if (disposed) return
          new EXRLoader().load(
            url,
            (tex) => {
              if (disposed) return
              tex.mapping = THREE.EquirectangularReflectionMapping
              setEnv(tex)
            },
            undefined,
            () => {
              if (disposed) return
              new THREE.TextureLoader().load(
                url,
                (tex) => {
                  tex.mapping = THREE.EquirectangularReflectionMapping
                  setEnv(tex)
                }
              )
            }
          )
        }
      )
    }

    return () => {
      disposed = true
      // Clear environment so other screens are unaffected
      scene.environment = null
      scene.background = null
      pmrem.dispose()
      if (currentEnvRT) {
        currentEnvRT.dispose()
      }
      if (currentBgTex) {
        currentBgTex.dispose()
      }
      revokeLater.forEach((u) => URL.revokeObjectURL(u))
    }
  }, [file, scene, gl])
  return null
}

function EnvironmentFromTexture({ texture, ground }: { texture: THREE.Texture, ground?: { height: number, radius: number, scale: number } }) {
  return (
    <Environment
      map={texture}
      background={!ground}
      ground={ground ? { height: ground.height, radius: ground.radius, scale: ground.scale } : undefined}
    />
  )
}